import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

interface MentionAutocompleteProps {
  query: string;
  visible: boolean;
  onSelect: (username: string) => void;
  onClose: () => void;
}

export default function MentionAutocomplete({ query, visible, onSelect, onClose }: MentionAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { data: results = [] } = useQuery<any[]>({
    queryKey: ["/api/users/search", { q: query }],
    queryFn: async () => {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(query)}`, { credentials: "include" });
      return res.json();
    },
    enabled: visible,
    staleTime: 30_000,
  });

  useEffect(() => setSelectedIndex(0), [results]);

  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && results[selectedIndex]) {
        e.preventDefault();
        onSelect(results[selectedIndex].username);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, results, selectedIndex, onSelect, onClose]);

  if (!visible || results.length === 0) return null;

  return (
    <div className="absolute left-0 right-0 z-50 bg-(--scroll-bg) border border-(--scroll-border) rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
      {results.slice(0, 5).map((user: any, i: number) => (
        <button
          key={user.id}
          type="button"
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
            i === selectedIndex ? "bg-(--scroll-bg-secondary)" : "hover:bg-(--scroll-bg-secondary)"
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(user.username);
          }}
        >
          <div className="w-7 h-7 rounded-full overflow-hidden shrink-0">
            {user.profileImageUrl ? (
              <img src={user.profileImageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted text-xs font-semibold text-muted-foreground">
                {(user.displayName || user.username || "?").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-(--scroll-text) truncate">{user.displayName || user.username}</p>
            <p className="text-xs text-(--scroll-text-tertiary) truncate">@{user.username}</p>
          </div>
        </button>
      ))}
    </div>
  );
}
